import {
  Appointment,
  TechnicianAppointmentsResult,
  UpdateAppointmentDto,
} from "@myapp/contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { appointmentKeys } from "../appointment.keys";
import { workOrderKeys } from "../../../work-order/api/work-order.keys";
import { appointmentApi } from "../service";
import { auditLogKeys } from "../../../audit-log";
import { rescheduleKeys } from "../../../reschedule/api/reschedule.keys";

export function useUpdateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      dto,
      workOrderId,
    }: {
      id: string;
      dto: UpdateAppointmentDto;
      workOrderId?: string;
    }) => {
      const { data } = await appointmentApi.update(id, dto);
      return { appointment: data as Appointment, workOrderId };
    },

    onMutate: async ({ id, dto, workOrderId: _workOrderId }) => {
      await queryClient.cancelQueries({ queryKey: appointmentKeys.all });

      const previousQueries = queryClient.getQueriesData<TechnicianAppointmentsResult>({
        queryKey: appointmentKeys.lists(),
      });

      queryClient.setQueriesData<TechnicianAppointmentsResult>(
        { queryKey: appointmentKeys.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            technicians: old.technicians.map((tech) => ({
              ...tech,
              appointments: tech.appointments.map((appt) => {
                if (appt.id !== id) return appt;
                return {
                  ...appt,
                  ...(dto.plannedStart !== undefined && { plannedStart: dto.plannedStart }),
                  ...(dto.assignedTechnicianId !== undefined && {
                    assignedTechnicianId: String(dto.assignedTechnicianId),
                  }),
                  ...(dto.tradeCode !== undefined && { tradeCode: dto.tradeCode }),
                  ...(dto.plannedTravelDuration !== undefined && {
                    plannedTravelDuration: dto.plannedTravelDuration,
                  }),
                  ...(dto.plannedWorkDuration !== undefined && {
                    plannedWorkDuration: dto.plannedWorkDuration,
                  }),
                  ...(dto.plannedDepartureDuration !== undefined && {
                    plannedDepartureDuration: dto.plannedDepartureDuration,
                  }),
                  ...(dto.attachments !== undefined && { attachments: dto.attachments }),
                  updatedAt: new Date().toISOString(),
                };
              }),
            })),
          };
        },
      );

      return { previousQueries };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },

    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: rescheduleKeys.count() });
      if (vars?.workOrderId) {
        queryClient.invalidateQueries({ queryKey: auditLogKeys.byWorkOrder(vars.workOrderId) });
      }
    },
  });
}
